import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { ChevronDown, Check } from 'lucide-react';

import { locales, localeToPath, type Locale } from '~/i18n/config';
import { useContent, useLocale } from '~/hooks/useContent';
import { navigateTo } from '~/lib/navigate';
import { cn } from '~/lib/cn';

const LOCALE_LABELS: Record<Locale, string> = {
  pl: 'PL',
  en: 'EN',
  de: 'DE',
};

const STORAGE_KEY = 'arpa:locale';

/**
 * Language switcher dropdown.
 *
 * - Trigger: shows current locale in uppercase + chevron.
 * - Menu: listbox pattern with PL / EN / DE options.
 * - Selection: writes to localStorage then navigates (full page reload to the
 *   SSG-prerendered page for that locale).
 * - Keyboard: Enter/Space open, ArrowDown/ArrowUp navigate, Enter selects,
 *   Escape closes.
 * - Avoids hydration mismatch: locale is derived from the URL via Vike's page
 *   context, not localStorage (which is only written on selection).
 */
function LanguageSwitcher(): ReactElement {
  const currentLocale = useLocale();
  const content = useContent();

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const triggerId = useId();
  const menuId = useId();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the focused option when it changes
  useEffect(() => {
    if (focusedIndex >= 0) {
      optionRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    const currentIdx = locales.indexOf(currentLocale);
    setFocusedIndex(currentIdx);
  }, [currentLocale]);

  const closeMenu = useCallback((returnFocus = true) => {
    setIsOpen(false);
    setFocusedIndex(-1);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  const selectLocale = useCallback(
    (locale: Locale) => {
      if (locale === currentLocale) {
        closeMenu();
        return;
      }
      try {
        localStorage.setItem(STORAGE_KEY, locale);
      } catch {
        // localStorage may be unavailable (private mode, storage quota, etc.)
      }
      navigateTo(localeToPath(locale));
    },
    [currentLocale, closeMenu]
  );

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }
      } else if (e.key === 'Escape') {
        closeMenu();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          setFocusedIndex((i) => Math.min(i + 1, locales.length - 1));
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          setFocusedIndex((i) => Math.max(i - 1, 0));
        }
      }
    },
    [isOpen, openMenu, closeMenu]
  );

  const handleOptionKeyDown = useCallback(
    (e: KeyboardEvent<HTMLLIElement>, locale: Locale, index: number) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectLocale(locale);
      } else if (e.key === 'Escape') {
        closeMenu();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(Math.min(index + 1, locales.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(Math.max(index - 1, 0));
      } else if (e.key === 'Tab') {
        // Tab closes the menu and lets focus move naturally
        closeMenu(false);
      }
    },
    [selectLocale, closeMenu]
  );

  const handleTriggerClick = useCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isOpen, closeMenu, openMenu]);

  return (
    <div className="relative inline-block">
      {/* Trigger */}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        aria-label={content.nav.languageLabel}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          'inline-flex items-center gap-1',
          'text-sm font-display font-semibold tracking-widest uppercase',
          'text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)]',
          'transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-void-navy)]',
          'rounded-sm px-1 py-0.5',
          isOpen && 'text-[var(--color-cyan-signal)]'
        )}
      >
        <span>{LOCALE_LABELS[currentLocale]}</span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          aria-hidden="true"
          className={cn('transition-transform duration-150', isOpen && 'rotate-180')}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <ul
          ref={menuRef}
          id={menuId}
          role="listbox"
          aria-label={content.nav.languageLabel}
          className={cn(
            'absolute right-0 top-full mt-2 z-[60]',
            'min-w-[5rem] py-1',
            'bg-[var(--color-deep-panel)] border border-[rgba(232,236,244,0.08)]',
            'rounded shadow-[0_8px_24px_-4px_rgba(0,0,0,0.5)]'
          )}
        >
          {locales.map((locale, index) => {
            const isCurrent = locale === currentLocale;
            const optionId = `${menuId}-option-${locale}`;
            return (
              <li
                key={locale}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                id={optionId}
                role="option"
                aria-selected={isCurrent}
                tabIndex={focusedIndex === index ? 0 : -1}
                onClick={() => {
                  selectLocale(locale);
                }}
                onKeyDown={(e) => {
                  handleOptionKeyDown(e, locale, index);
                }}
                className={cn(
                  'flex items-center justify-between',
                  'px-4 py-2 cursor-pointer select-none',
                  'text-sm font-display font-semibold tracking-widest uppercase',
                  'transition-colors duration-100',
                  isCurrent ? 'text-[var(--color-cyan-signal)]' : 'text-[var(--color-frost-white)]',
                  'hover:bg-[rgba(0,200,255,0.06)] focus:bg-[rgba(0,200,255,0.06)]',
                  'focus-visible:outline-none'
                )}
              >
                <span>{LOCALE_LABELS[locale]}</span>
                {isCurrent && (
                  <Check size={12} strokeWidth={2.5} aria-hidden="true" className="ml-2" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export { LanguageSwitcher };
export default LanguageSwitcher;
